// src/app/components/layout/PageHeader.tsx
//
// Barra única: a página põe título, abas e ações dentro da TopBar (no lugar
// do breadcrumb "Home / …") em vez de empilhar um cabeçalho próprio abaixo
// dela. No notebook (~720px de altura) isso devolve ~90px para o conteúdo.
//
// Uso:  <PageHeader title="Captação" description="…" tabs={<CaptacaoTabs />} actions={<Button…/>} />
// A TopBar mostra o breadcrumb só quando nenhuma página montou um PageHeader.

import {
  createContext, useContext, useEffect, useState, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

interface PageHeaderHost {
  slot: HTMLElement | null;
  setSlot: (el: HTMLElement | null) => void;
  active: number;
  register: () => () => void;
}

const PageHeaderContext = createContext<PageHeaderHost | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [active, setActive] = useState(0);
  const register = () => {
    setActive((n) => n + 1);
    return () => setActive((n) => n - 1);
  };
  return (
    <PageHeaderContext.Provider value={{ slot, setSlot, active, register }}>
      {children}
    </PageHeaderContext.Provider>
  );
}

/** Para a TopBar: onde a página desenha e se alguma página está desenhando. */
export function usePageHeaderHost() {
  return useContext(PageHeaderContext);
}

interface PageHeaderProps {
  title: string;
  /** Vira dica ao passar o mouse no título (era o subtítulo fixo). */
  description?: string;
  tabs?: ReactNode;
  actions?: ReactNode;
  /** Antes do título — ex.: botão voltar, bolinha de cor do setor. */
  leading?: ReactNode;
  /** Logo depois do título — ex.: estrela de favoritar. */
  titleExtra?: ReactNode;
}

export function PageHeader({ title, description, tabs, actions, leading, titleExtra }: PageHeaderProps) {
  const host = useContext(PageHeaderContext);
  const register = host?.register;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => register?.(), []);
  if (!host?.slot) return null;

  return createPortal(
    <div data-page-header className="flex items-center gap-4 lg:gap-5 min-w-0 flex-1 h-full">
      <div className="flex items-center gap-2 min-w-0">
        {leading}
        <h1
          title={description}
          className="text-[15px] font-semibold text-slate-900 dark:text-slate-100 whitespace-nowrap truncate"
        >
          {title}
        </h1>
        {titleExtra}
      </div>
      {tabs && (
        <>
          <span aria-hidden="true" className="hidden sm:block h-5 w-px bg-slate-200 dark:bg-white/10" />
          <div className="self-stretch min-w-0 overflow-x-auto">{tabs}</div>
        </>
      )}
      {actions && <div className="ml-auto flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>,
    host.slot,
  );
}
